import { useEffect, useRef } from "react";
import type { CardTheme } from "@/components/cards/card-theme";

export const CARD_CLASS =
  "relative border border-white/20 rounded-xl w-[170px] aspect-[5/7] overflow-hidden";
export const CARD_ASPECT = 7 / 5;

/** Taller card (fits a stat/skill block, e.g. pokemon cards). */
export const TALL_CARD_CLASS =
  "relative border border-white/20 rounded-xl w-[170px] aspect-[5/7.6] overflow-hidden";
export const TALL_CARD_ASPECT = 7.6 / 5;

/** Compact pokemon card (no attacks/footer). Height follows content. */
export const MINI_CARD_CLASS =
  "relative border border-white/20 rounded-xl w-[170px] overflow-hidden";

const FULL_CARD_WIDTH = 170;

/** Shrinks a full-size card (170px wide) down to `width` for grid display, via CSS scale. */
export const MiniCard = ({
  width,
  aspect = TALL_CARD_ASPECT,
  children,
}: {
  width: number;
  aspect?: number;
  children: React.ReactNode;
}) => {
  const scale = width / FULL_CARD_WIDTH;
  return (
    <div style={{ width, height: width * aspect, overflow: "hidden" }}>
      <div
        style={{
          width: FULL_CARD_WIDTH,
          height: FULL_CARD_WIDTH * aspect,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const cardBackground = (t: CardTheme): React.CSSProperties => ({
  background: `linear-gradient(180deg, ${t.dark} 0%, ${t.main} 120%)`,
  boxShadow: `0 8px 16px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(0,0,0,0.8), 0 0 24px ${t.main}44`,
});

/** Positions the tilt vars from a normalized (0..1) pointer coordinate. */
const applyTilt = (el: HTMLDivElement, px: number, py: number) => {
  el.style.setProperty("--rx", `${((py - 0.5) * -24).toFixed(2)}deg`);
  el.style.setProperty("--ry", `${((px - 0.5) * 24).toFixed(2)}deg`);
  el.style.setProperty("--mx", `${(px * 100).toFixed(1)}%`);
  el.style.setProperty("--my", `${(py * 100).toFixed(1)}%`);
  el.style.setProperty("--o", "1");
  el.style.setProperty("--s", "1.08");
};

// Four corners the auto-shine sweep walks through (top-left → top-right →
// bottom-right → bottom-left), kept slightly inset for a softer sweep.
const SHINE_CORNERS: [number, number][] = [
  [0.15, 0.15],
  [0.85, 0.15],
  [0.85, 0.85],
  [0.15, 0.85],
];

/**
 * 3D tilt wrapper — sets CSS vars (--rx/--ry rotation, --mx/--my pointer
 * position, --o hover intensity, --s scale) consumed by the foil/glare layers.
 * When `autoShine` is set, it slowly sweeps the glare corner-to-corner on a
 * loop (as if hovered), ignoring pointer input.
 */
export const TiltCard = ({
  children,
  autoShine = false,
}: {
  children: React.ReactNode;
  autoShine?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (autoShine) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    applyTilt(el, (e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
  };

  const handleLeave = () => {
    if (autoShine) return;
    resetTilt(ref.current);
  };

  useEffect(() => {
    if (!autoShine) return;
    const el = ref.current;
    if (!el) return;

    const segmentMs = 1400; // time to travel between two corners
    let raf = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.max(0, (now - start) / segmentMs);
      const i = Math.floor(t) % SHINE_CORNERS.length;
      const [ax, ay] = SHINE_CORNERS[i]!;
      const [bx, by] = SHINE_CORNERS[(i + 1) % SHINE_CORNERS.length]!;
      // ease-in-out between the current corner pair
      const f = t - Math.floor(t);
      const e = f < 0.5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2;
      applyTilt(el, ax + (bx - ax) * e, ay + (by - ay) * e);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      resetTilt(ref.current);
    };
  }, [autoShine]);

  return (
    <div className="relative hover:z-20 shrink-0" style={{ perspective: 700 }}>
      <div
        ref={ref}
        onPointerMove={handleMove}
        onPointerLeave={handleLeave}
        style={{
          transform: "rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(var(--s, 1))",
          transformStyle: "preserve-3d",
          transition: "transform 150ms ease-out",
          willChange: "transform",
        }}
      >
        {children}
      </div>
    </div>
  );
};

const resetTilt = (el: HTMLDivElement | null) => {
  if (!el) return;
  el.style.setProperty("--rx", "0deg");
  el.style.setProperty("--ry", "0deg");
  el.style.setProperty("--mx", "50%");
  el.style.setProperty("--my", "50%");
  el.style.setProperty("--o", "0");
  el.style.setProperty("--s", "1");
};

export const Backdrop = ({ t }: { t: CardTheme }) => (
  <>
    <div
      className="absolute inset-0"
      style={{
        background: `
          radial-gradient(ellipse 90% 50% at 50% 30%, ${t.light}55 0%, transparent 60%),
          linear-gradient(115deg, transparent 40%, ${t.light}22 50%, transparent 60%)
        `,
      }}
    />
    <div className="absolute inset-[5px] border border-white/25 rounded-lg pointer-events-none" />
    <div className="absolute inset-[8px] border border-white/10 rounded-md pointer-events-none" />
  </>
);

export const Scrim = ({ children }: { children: React.ReactNode }) => (
  <div className="absolute inset-x-0 bottom-0 z-10">
    <div
      className="mx-[7px] mb-[7px] px-2 pt-1.5 pb-2 rounded-lg backdrop-blur-sm"
      style={{ background: "rgba(6,6,14,0.55)", border: "1px solid rgba(255,255,255,0.15)" }}
    >
      {children}
    </div>
  </div>
);

/** Art-matched diagonal light stripes (colors extracted from the card art). */
export const LightStripes = ({ colors }: { colors: string[] }) => {
  if (colors.length === 0) return null;
  return (
    <div
      className="absolute inset-0 mix-blend-screen"
      style={{
        background: colors
          .map(
            (color, i) =>
              `linear-gradient(${100 + i * 35}deg, transparent 40%, ${color}55 50%, transparent 60%)`,
          )
          .join(", "),
      }}
    />
  );
};

export const FULL_ART_BG_URL = "/assets/cards/premium-bg/swirl-violet.png";
